import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { ISftpPartner } from '@core/models/sftp-transfer.model';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import epcisService from 'services/api/epcis.service';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import OutboundValidationPanel from 'components/integrations/OutboundValidationPanel';
import { integrationErrorMessage } from '../Integrations/integration-user-messages';

const inputClass =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#bd902e] focus:outline-none focus:ring-2 focus:ring-[#bd902e]/30 disabled:bg-slate-50';

const SCHEMA_OPTIONS = [
    { value: '1.2', label: 'EPCIS 1.2 (default)' },
    { value: '1.0', label: 'EPCIS 1.0' },
    { value: '2.0', label: 'EPCIS 2.0' },
];

type EpcisRow = {
    id?: number;
    documentId?: string;
    email?: string;
    productBatchId?: number;
};

function epcisOptionLabel(row: EpcisRow): string {
    const parts = [`#${row.id}`];
    if (row.documentId) parts.push(row.documentId);
    if (row.productBatchId) parts.push(`batch ${row.productBatchId}`);
    if (row.email) parts.push(row.email);
    return parts.join(' · ');
}

const SftpEpcisDispatchPanel: FC = () => {
    const [partners, setPartners] = useState<ISftpPartner[]>([]);
    const [epcisRows, setEpcisRows] = useState<EpcisRow[]>([]);
    const [loadingEpcis, setLoadingEpcis] = useState(false);
    const [partnerCode, setPartnerCode] = useState('');
    const [epcisId, setEpcisId] = useState('');
    const [schemaVersion, setSchemaVersion] = useState('1.2');
    const [sending, setSending] = useState(false);
    const [validationFailed, setValidationFailed] = useState(false);

    const loadEpcis = useCallback(async () => {
        setLoadingEpcis(true);
        try {
            const res: any = await epcisService.getEpcis({
                page: 0,
                size: 50,
                email: '',
                ndcCode: '',
                lotNumber: '',
                itemName: '',
                expiredOnly: false,
                startDate: '',
                endDate: '',
            });
            const content = (res.data?.epcis?.content || []) as EpcisRow[];
            setEpcisRows(content.filter((r) => r.id != null));
        } catch {
            toast.error('Could not load EPCIS records.', { autoClose: 5000 });
        } finally {
            setLoadingEpcis(false);
        }
    }, []);

    useEffect(() => {
        sftpIntegrationService
            .listIntegrations()
            .then((r: any) => setPartners(r.data?.integrations || r.data?.sftpPartners || []))
            .catch(() => {});
        loadEpcis();
    }, [loadEpcis]);

    const partnerOptions = useMemo(
        () =>
            partners.filter(
                (p) =>
                    p.enabled !== false &&
                    p.code &&
                    (p.direction === 'SEND' || p.direction === 'BIDIRECTIONAL')
            ),
        [partners]
    );

    useEffect(() => {
        if (partnerCode || partnerOptions.length === 0) return;
        setPartnerCode(partnerOptions[0].code || '');
    }, [partnerCode, partnerOptions]);

    const dispatch = async () => {
        if (!partnerCode) {
            toast.error('Choose an SFTP partner.', { autoClose: 5000 });
            return;
        }
        if (!epcisId) {
            toast.error('Choose an EPCIS record.', { autoClose: 5000 });
            return;
        }
        if (validationFailed) {
            toast.error('Outbound L4 validation failed — fix the document before dispatch.', { autoClose: 8000 });
            return;
        }
        setSending(true);
        try {
            await sftpIntegrationService.dispatch({
                epcisId: Number(epcisId),
                partnerCode,
                schemaVersion,
            });
            toast.success(`EPCIS #${epcisId} dispatched to ${partnerCode} via SFTP.`, { autoClose: 8000 });
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not dispatch SFTP EPCIS.'), { autoClose: 8000 });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-md border border-[#bd902e]/30 bg-amber-50/40 p-4">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h4 className="text-sm font-semibold text-slate-900">Send EPCIS via SFTP</h4>
                    <p className="mt-0.5 max-w-2xl text-xs text-slate-600">
                        Uploads real outbound EPCIS to a SEND/BIDIRECTIONAL partner (remote client upload or Antar-hosted
                        outbox). Use <strong>Send test</strong> on the partner row for connectivity only.
                    </p>
                </div>
                <Link to={PRIVATEPATHS.Epcis} className="shrink-0 text-xs font-semibold text-[#bd902e] hover:underline">
                    Full EPCIS list →
                </Link>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">SFTP partner</label>
                    <select className={inputClass} value={partnerCode} onChange={(e) => setPartnerCode(e.target.value)}>
                        <option value="">— Select partner —</option>
                        {partnerOptions.map((p) => (
                            <option key={p.code} value={p.code!}>
                                {p.code} — {p.name}
                                {p.deliveryMode === 'HOSTED' ? ' (hosted)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">EPCIS record</label>
                    <select
                        className={inputClass}
                        value={epcisId}
                        disabled={loadingEpcis}
                        onChange={(e) => setEpcisId(e.target.value)}
                    >
                        <option value="">{loadingEpcis ? 'Loading…' : '— Select EPCIS —'}</option>
                        {epcisRows.map((row) => (
                            <option key={row.id} value={String(row.id)}>
                                {epcisOptionLabel(row)}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">Schema version</label>
                    <select className={inputClass} value={schemaVersion} onChange={(e) => setSchemaVersion(e.target.value)}>
                        {SCHEMA_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-end gap-2">
                    <button
                        type="button"
                        disabled={sending || loadingEpcis || validationFailed}
                        onClick={dispatch}
                        className="rounded-md bg-[#bd902e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a67f28] disabled:opacity-50"
                    >
                        {sending ? 'Sending…' : 'Send via SFTP'}
                    </button>
                    <button
                        type="button"
                        onClick={loadEpcis}
                        disabled={loadingEpcis}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                        Refresh
                    </button>
                </div>
            </div>
            {epcisId ? (
                <div className="mt-3">
                    <OutboundValidationPanel
                        epcisId={Number(epcisId)}
                        compact
                        onResult={(r) =>
                            setValidationFailed(
                                r?.compliant === false || r?.status === 'FAILED'
                            )
                        }
                    />
                </div>
            ) : null}
            <p className="mt-2 text-xs text-amber-900">
                Dispatch is blocked when outbound L4 validation reports FAILED.
            </p>
        </div>
    );
};

export default SftpEpcisDispatchPanel;
